import { ANY_AIB_REGISTRY_SENTINEL } from "@/constants";
import type {
  CancellationRegistryOptionsResponse,
  CancellationTargetOption,
} from "@/dashboard/inventory_management/common/cancellationTargetTypes";
import { translateCountryCode, type TranslationFn } from "@/utils/countries";

/**
 * Answers what the cancellation forms need to know about a registry dropdown value, from the backend's
 * registry options. Every method takes the dropdown value as is, so the "Any AIB" sentinel resolves to its
 * effective region (Norway) in one place.
 *
 * Before the options load, no region is known: lookups return undefined/false and the sentinel has no
 * effective region. Forms must therefore not submit until `isLoaded`.
 */
export interface CancellationTargetLookup {
  readonly isLoaded: boolean;
  readonly targets: readonly CancellationTargetOption[];
  /** The region a dropdown value is submitted as; "" while the sentinel can't be resolved yet. */
  effectiveRegion(registryCountryRegion: string | null | undefined): string;
  target(
    registryCountryRegion: string | null | undefined
  ): CancellationTargetOption | undefined;
  requiresAddress(registryCountryRegion: string | null | undefined): boolean;
  /** Whether the registry (the Grexel platform) can keep a cancellation statement private (DEV-583). */
  supportsPrivateStatement(
    registryCountryRegion: string | null | undefined
  ): boolean;
  /** Whether the statement goes to TGE (Poland), whose redemption form needs the legal form and takes a VAT number. */
  usesTgeOptions(registryCountryRegion: string | null | undefined): boolean;
  /**
   * Whether Soldera can't cancel in the region from its own account, so the user picks how the cancellation is
   * arranged (DEV-626 change 8). False for an unknown region, as the backend records no mode for one.
   */
  requiresCancellationMode(
    registryCountryRegion: string | null | undefined
  ): boolean;
  /**
   * A dropdown value as the cancellation forms name it: "Germany (HKNR)" for a known target, the country name
   * for an unknown region, the "Any AIB" option's own label for the sentinel, and "-" for none.
   */
  label(
    registryCountryRegion: string | null | undefined,
    t: TranslationFn
  ): string;
  /** The target regions as dropdown options, sorted by label. Every region stays selectable: the rules only warn (DEV-626). */
  dropdownOptions(t: TranslationFn): { value: string; label: string }[];
}

export const createCancellationTargetLookup = (
  options: CancellationRegistryOptionsResponse | null
): CancellationTargetLookup => {
  const targets = options?.targets ?? [];
  const targetsByRegion = new Map(
    targets.map((target) => [target.region, target])
  );

  const effectiveRegion = (
    registryCountryRegion: string | null | undefined
  ): string => {
    if (registryCountryRegion === ANY_AIB_REGISTRY_SENTINEL) {
      return options?.any_aib.effective_region ?? "";
    }
    return registryCountryRegion ?? "";
  };

  const target = (registryCountryRegion: string | null | undefined) =>
    targetsByRegion.get(effectiveRegion(registryCountryRegion));

  const label = (
    registryCountryRegion: string | null | undefined,
    t: TranslationFn
  ): string => {
    if (!registryCountryRegion) return "-";
    if (registryCountryRegion === ANY_AIB_REGISTRY_SENTINEL) {
      return t("go_cancel.any_aib_option");
    }
    const countryName = translateCountryCode(registryCountryRegion, t);
    const operator = targetsByRegion.get(registryCountryRegion)?.operator;
    return operator ? `${countryName} (${operator})` : countryName;
  };

  return {
    isLoaded: options !== null,
    targets,
    effectiveRegion,
    target,
    requiresAddress: (region) => target(region)?.requires_address ?? false,
    supportsPrivateStatement: (region) =>
      target(region)?.supports_private_statement ?? false,
    usesTgeOptions: (region) => target(region)?.uses_tge_options ?? false,
    requiresCancellationMode: (region) =>
      (target(region)?.status ?? "direct") !== "direct",
    label,
    dropdownOptions: (t) =>
      targets
        .map(({ region }) => ({ value: region, label: label(region, t) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
  };
};
